// Revision paper attachments — hosting the original exam PDFs so students can
// download them alongside the interactive.
//
// These are published exam papers (public data), so they live in the existing
// PUBLIC `course-assets` bucket under a `revision-papers/` prefix. No new bucket,
// no migration: the bucket's policies already allow INSTRUCTOR/ADMIN writes and
// public reads (supabase/migrations/20250101000021_storage_bucket.sql).
//
// The upload goes browser → Storage DIRECTLY, not through /api/designer/upload.
// That matters: the function caps bodies at 10MB and scanned past papers routinely
// run 15–40MB. The extractor then reads the bytes back server-side from the URL,
// so the file crosses the wire once instead of twice.

import { supabase } from '../supabase';
import type { RevisionExamAttachment } from '../../components/course-builder/slides/slideTypes';

const BUCKET = 'course-assets';
const PREFIX = 'revision-papers';

/**
 * Client-side ceiling. Supabase enforces its own per-project limit (50MB on the
 * default plan) — this exists to fail fast with a readable message rather than a
 * bare 413 after a long upload.
 */
export const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;

const ALLOWED_EXT = ['pdf', 'docx', 'txt', 'md'];

export class AttachmentUploadError extends Error {
  constructor(public code: string, message: string) {
    super(message);
    this.name = 'AttachmentUploadError';
  }
}

export interface UploadedAttachment {
  url: string;
  fileName: string;
  sizeBytes: number;
}

/** Human-readable size, for the download chip. */
export function formatBytes(bytes: number | undefined): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The URL that actually saves the file rather than opening it in a tab.
 * Supabase turns `?download=<name>` into a `Content-Disposition: attachment`
 * header — the only reliable way to force a save on iOS Safari, where the HTML
 * `download` attribute is ignored cross-origin.
 */
export function attachmentDownloadUrl(a: Pick<RevisionExamAttachment, 'url' | 'fileName'>): string {
  const sep = a.url.includes('?') ? '&' : '?';
  return `${a.url}${sep}download=${encodeURIComponent(a.fileName)}`;
}

/** Upload one revision document to the public bucket and return its URL. */
export async function uploadRevisionDoc(file: File): Promise<UploadedAttachment> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXT.includes(ext)) {
    throw new AttachmentUploadError('unsupported_type', 'Upload a PDF, DOCX, TXT, or MD file.');
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    throw new AttachmentUploadError(
      'too_large',
      `That file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_ATTACHMENT_BYTES)} — try a compressed or text-layer PDF.`,
    );
  }

  const path = `${PREFIX}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    contentType: file.type || undefined,
    // Papers are content-addressed by UUID, so a collision means a retry of the
    // same upload — overwriting is safe and saves an orphan.
    upsert: true,
  });
  if (error) {
    throw new AttachmentUploadError('upload_failed', error.message || 'Could not upload the file.');
  }

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  if (!data?.publicUrl) {
    throw new AttachmentUploadError('no_url', 'Upload succeeded but no public URL was returned.');
  }

  return { url: data.publicUrl, fileName: file.name, sizeBytes: file.size };
}

/** Attachments a student may see right now, given whether they've finished. */
export function visibleAttachments(
  attachments: RevisionExamAttachment[] | undefined,
  phase: 'during' | 'after-results',
): RevisionExamAttachment[] {
  if (!attachments?.length) return [];
  return attachments.filter((a) =>
    a.visibility === 'always' ? true : a.visibility === 'after-results' ? phase === 'after-results' : false,
  );
}
